import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BrandLogo } from '@/components/brand/BrandLogo';

const TOS_VERSION = '2026-06-25';

/**
 * Wraps authenticated content and blocks access until the user
 * has accepted the current Terms of Service. Shown as an interstitial
 * for existing users who signed up before ToS was added.
 */
export function TosGate({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const login = useAuthStore((s) => s.login);
  const token = useAuthStore((s) => s.token);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If user has already accepted ToS, render children
  if (user?.tosAcceptedAt) {
    return <>{children}</>;
  }

  async function handleAccept() {
    if (!accepted || !token || !user) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await api.post<{ user: typeof user }>('/auth/accept-tos', {
        tosVersion: TOS_VERSION,
      });
      // Update the auth store with the updated user profile
      login(token, res.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept Terms of Service');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-900 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <BrandLogo size="lg" />
          </div>
          <CardTitle className="text-xl">Terms of Service Update</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            We've added Terms of Service and a Privacy Policy. Please review and
            accept them to continue using RegattaReplay™.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-2">
            <input
              id="tos-interstitial"
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-ink focus:ring-brand-ink"
            />
            <label
              htmlFor="tos-interstitial"
              className="text-sm text-muted-foreground leading-tight"
            >
              I agree to the{' '}
              <Link
                to="/terms"
                target="_blank"
                className="text-brand-ink hover:underline"
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                to="/privacy"
                target="_blank"
                className="text-brand-ink hover:underline"
              >
                Privacy Policy
              </Link>
            </label>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <Button
            onClick={handleAccept}
            className="w-full bg-brand-blue !text-navy-900 hover:bg-brand-dark"
            disabled={!accepted || submitting}
          >
            {submitting ? 'Accepting...' : 'Continue'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
